import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import accessApi from '../services/accessApi';
import enrichmentApi from '../services/enrichmentApi';
import { formatDateTime, timeAgo } from '../utils/time';
import { useAuth } from './AuthGate';
import FindingCard, { SourceLink } from './FindingCard';
import Icon from './Icon';
import IcpSegmentsTab from './IcpSegmentsTab';
import LinkedInImportTab from './LinkedInImportTab';
import { managerInitials } from './RelationshipManagersField';
import './RelationshipManagers.css';
import './AdminPanel.css';

const ROLE_HINTS = {
  admin: 'Everything, including users and the relationship-manager list',
  relationship_manager: 'Browse, edit people, LinkedIn PDFs, who manages a company'
};

// Activity filters: prefixes of the logged actions
const ACTIVITY_FILTERS = [
  { value: '', label: 'All changes' },
  { value: 'contact.', label: 'Edits to people' },
  { value: 'profile.', label: 'LinkedIn PDFs saved' },
  { value: 'person.', label: 'People added' },
  { value: 'company.', label: 'Companies: who manages them, and their ICP' },
  { value: 'icp_segment.', label: 'ICP segments' },
  { value: 'relationship_manager.', label: 'Relationship-manager list' },
  { value: 'user.', label: 'Users and access' },
  { value: 'import.', label: 'Imports' }
];

// Longer lists of changes are bulk ones, so they open on request
const LONGEST_OPEN_LIST = 6;

const isEmpty = (value) => value === null || value === undefined || value === '';

// A row labelled with its own new value is a merge: the old forms all became that value
const isMerge = (change) => !isEmpty(change.before) && change.field === change.after;

// Rows that each drop one thing under the same label read better as one row
const gatherRemovals = (changes) => changes.reduce((rows, change) => {
  const last = rows[rows.length - 1];
  if (last && last.field === change.field && isEmpty(last.after) && isEmpty(change.after)) {
    rows[rows.length - 1] = { ...last, before: `${last.before}, ${change.before}`, gathered: true };
  } else {
    rows.push(change);
  }
  return rows;
}, []);

// "before → after"; just the new value when there was none, the old one struck through when it went
const ChangeValue = ({ change }) => {
  if (isEmpty(change.before)) return <span>{isEmpty(change.after) ? '—' : String(change.after)}</span>;
  if (isEmpty(change.after)) {
    return <><span className="adm-event__before">{String(change.before)}</span> <span className="adm-event__removed">removed</span></>;
  }
  return (
    <>
      <span className="adm-event__before">{String(change.before)}</span>
      <Icon name="chevron-right" />
      <span>{String(change.after)}</span>
    </>
  );
};

const ChangeRow = ({ change }) => {
  if (isMerge(change) || change.gathered) {
    return (
      <div className="adm-event__change adm-event__change--list">
        <dt>{change.field}</dt>
        <dd>{isMerge(change) ? `from ${change.before}` : change.before}</dd>
      </div>
    );
  }
  return (
    <div className="adm-event__change">
      <dt>{change.field}</dt>
      <dd><ChangeValue change={change} /> {change.source && <SourceLink url={change.source} />}</dd>
    </div>
  );
};

/** One logged change: who, when, what, and the field-by-field before and after. */
export const ActivityEntry = ({ event }) => {
  const [open, setOpen] = useState(false);
  const changes = gatherRemovals(Array.isArray(event.changes) ? event.changes : []);
  const long = changes.length > LONGEST_OPEN_LIST;
  return (
    <li className="adm-event">
      <div className="adm-event__line">
        <span className="adm-event__who">{event.actor.name}</span>
        <span className="adm-event__when" title={formatDateTime(event.occurred_at)}>{timeAgo(event.occurred_at)}</span>
      </div>
      <div className="adm-event__summary">{event.summary}</div>
      {long && (
        <button type="button" className="adm-event__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          <Icon name={open ? 'chevron-down' : 'chevron-right'} />
          {open ? 'Hide details' : 'Show details'}
        </button>
      )}
      {changes.length > 0 && (!long || open) && (
        <dl className={`adm-event__changes${long ? ' adm-event__changes--long' : ''}`}>
          {changes.map((change, index) => <ChangeRow key={`${change.field}-${index}`} change={change} />)}
        </dl>
      )}
    </li>
  );
};

const UsersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);
  const [form, setForm] = useState({ email: '', name: '', roles: ['relationship_manager'], sendWelcome: true });
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [teamDomain, setTeamDomain] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await accessApi.listUsers();
      setUsers(data.users);
      setRoles(data.roles);
      setEmailEnabled(Boolean(data.email_enabled));
      setTeamDomain(data.team_domain);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = async (user, request) => {
    setBusyId(user.id);
    setRowError(null);
    try {
      const updated = await request();
      setUsers(current => current.map(row => (row.id === updated.id ? updated : row)));
    } catch (err) {
      setRowError({ id: user.id, message: err.message });
    } finally {
      setBusyId(null);
    }
  };

  const change = (user, changes) => updateRow(user, () => accessApi.updateUser(user.id, changes));
  const sendWelcome = (user) => updateRow(user, () => accessApi.sendWelcome(user.id));

  const toggleRole = (user, role) => change(user, {
    roles: user.roles.includes(role) ? user.roles.filter(name => name !== role) : [...user.roles, role]
  });

  const add = async (event) => {
    event.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      await accessApi.addUser({
        email: form.email.trim(),
        name: form.name.trim() || null,
        roles: form.roles,
        send_welcome: emailEnabled && form.sendWelcome
      });
      setForm({ email: '', name: '', roles: ['relationship_manager'], sendWelcome: true });
      await load();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="adm-tab">
      <form className="adm-add" onSubmit={add} aria-label="Give someone access">
        <div className="rm-form__field">
          <label htmlFor="adm-add-email">Google account email</label>
          <input id="adm-add-email" type="email" className="rm-input" value={form.email} required
                 aria-describedby="adm-add-hint"
                 onChange={(event) => setForm(current => ({ ...current, email: event.target.value }))} />
        </div>
        <div className="rm-form__field">
          <label htmlFor="adm-add-name">Name</label>
          <input id="adm-add-name" className="rm-input" value={form.name} maxLength={120}
                 onChange={(event) => setForm(current => ({ ...current, name: event.target.value }))} />
        </div>
        <fieldset className="adm-add__roles">
          <legend>Roles</legend>
          {roles.map(role => (
            <label key={role.id} className="adm-role" title={ROLE_HINTS[role.id]}>
              <input
                type="checkbox"
                checked={form.roles.includes(role.id)}
                onChange={() => setForm(current => ({
                  ...current,
                  roles: current.roles.includes(role.id)
                    ? current.roles.filter(name => name !== role.id)
                    : [...current.roles, role.id]
                }))}
              />
              {role.label}
            </label>
          ))}
        </fieldset>
        <button type="submit" className="rm-button rm-button--primary" disabled={adding || !form.email.trim()}>
          <Icon name="plus" /> {adding ? 'Adding...' : 'Give access'}
        </button>
        {teamDomain && (
          <span id="adm-add-hint" className="rm-form__hint adm-add__hint">Sinatra is only for @{teamDomain} accounts.</span>
        )}
        {emailEnabled && (
          <label className="adm-role adm-add__welcome">
            <input
              type="checkbox"
              checked={form.sendWelcome}
              onChange={() => setForm(current => ({ ...current, sendWelcome: !current.sendWelcome }))}
            />
            Send a welcome email with how to sign in and get started
          </label>
        )}
        {addError && <p className="rm-error adm-add__error" role="alert"><Icon name="alert-triangle" /> {addError}</p>}
      </form>

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!users && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {users && (
        <ul className="adm-users">
          {users.map(user => {
            const waiting = user.active && user.roles.length === 0;
            return (
              <li key={user.id} className={`adm-user${user.active ? '' : ' adm-user--off'}`}>
                {user.picture_url ? (
                  <img className="rm-avatar adm-user__picture" src={user.picture_url} alt="" referrerPolicy="no-referrer" />
                ) : (
                  <span className="rm-avatar" aria-hidden="true">{managerInitials(user.name || user.email)}</span>
                )}
                <div className="adm-user__main">
                  <div className="adm-user__name">
                    {user.name || user.email}
                    {user.id === me.id && <span className="rm-badge">You</span>}
                    {waiting && <span className="rm-badge rm-badge--new">Asking for access</span>}
                    {!user.active && <span className="rm-badge">Access off</span>}
                  </div>
                  {user.name && <div className="rm-row__meta">{user.email}</div>}
                  <div className="rm-row__meta">
                    {user.last_sign_in_at
                      ? <>Last signed in <span title={formatDateTime(user.last_sign_in_at)}>{timeAgo(user.last_sign_in_at)}</span></>
                      : 'Never signed in'}
                  </div>
                  {emailEnabled && user.welcome_sent_at && !user.welcome_error && (
                    <div className="rm-row__meta">
                      Welcome email sent <span title={formatDateTime(user.welcome_sent_at)}>{timeAgo(user.welcome_sent_at)}</span>
                    </div>
                  )}
                  {user.welcome_error && (
                    <p className="rm-error" role="alert"><Icon name="alert-triangle" /> Welcome email not sent. {user.welcome_error}</p>
                  )}
                  {rowError?.id === user.id && (
                    <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {rowError.message}</p>
                  )}
                </div>
                <div className="adm-user__side">
                  <fieldset className="adm-user__roles" disabled={busyId === user.id}>
                    <legend className="rm-visually-hidden">Roles of {user.name || user.email}</legend>
                    {roles.map(role => (
                      <label key={role.id} className="adm-role" title={ROLE_HINTS[role.id]}>
                        <input type="checkbox" checked={user.roles.includes(role.id)} onChange={() => toggleRole(user, role.id)} />
                        {role.label}
                      </label>
                    ))}
                  </fieldset>
                  <button
                    type="button"
                    className="rm-button rm-button--quiet"
                    disabled={busyId === user.id}
                    onClick={() => change(user, { active: !user.active })}
                  >
                    {user.active ? 'Turn access off' : 'Turn access on'}
                  </button>
                  {emailEnabled && user.active && user.roles.length > 0 && (
                    <button
                      type="button"
                      className="rm-button rm-button--quiet"
                      disabled={busyId === user.id}
                      onClick={() => sendWelcome(user)}
                    >
                      <Icon name="mail" /> {user.welcome_sent_at ? 'Send welcome email again' : 'Send welcome email'}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ActivityTab = () => {
  const [filter, setFilter] = useState('');
  const [events, setEvents] = useState(null);
  const [nextBefore, setNextBefore] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEvents(null);
    setError(null);
    accessApi.activity({ action: filter || undefined, limit: 50 })
      .then(data => {
        if (cancelled) return;
        setEvents(data.events);
        setNextBefore(data.next_before_id);
      })
      .catch(err => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const more = async () => {
    setLoadingMore(true);
    try {
      const data = await accessApi.activity({ action: filter || undefined, limit: 50, before_id: nextBefore });
      setEvents(current => [...current, ...data.events]);
      setNextBefore(data.next_before_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="adm-tab">
      <div className="adm-filter">
        <label htmlFor="adm-activity-filter">Show</label>
        <select id="adm-activity-filter" className="rm-input" value={filter} onChange={(event) => setFilter(event.target.value)}>
          {ACTIVITY_FILTERS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!events && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {events && events.length === 0 && <p className="rm-empty">Nothing yet.</p>}
      {events && events.length > 0 && (
        <ul className="adm-events">
          {events.map(event => <ActivityEntry key={event.id} event={event} />)}
        </ul>
      )}
      {nextBefore && (
        <button type="button" className="rm-button adm-more" onClick={more} disabled={loadingMore}>
          {loadingMore ? 'Loading...' : 'Show more'}
        </button>
      )}
    </div>
  );
};

/** For admins: who can sign in and with which roles, and the log of every change. */
const AGENT_POLL_MS = 5000;

const SOURCE_ROWS = [
  ['company_sites', 'Company websites', null],
  ['web_search', 'Web search', 'SERPAPI_API_KEY'],
  ['apollo', 'Apollo', 'APOLLO_API_KEY']
];

const runProgress = (run) => [
  `${run.done} of ${run.total} done`,
  run.filled ? `${run.filled} filled in` : null,
  run.proposed ? `${run.proposed} for review` : null,
  run.failed ? `${run.failed} failed` : null
].filter(Boolean).join(' · ');

const AgentsTab = () => {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [findings, setFindings] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [nextStatus, nextRuns, nextFindings] = await Promise.all([
        enrichmentApi.status(), enrichmentApi.runs(), enrichmentApi.waitingForReview()
      ]);
      setStatus(nextStatus);
      setRuns(nextRuns.runs);
      setFindings(nextFindings.findings);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const working = Boolean(status?.queued);
  useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(load, AGENT_POLL_MS);
    return () => clearInterval(timer);
  }, [working, load]);

  const act = async (operation) => {
    setBusy(true);
    try {
      await operation();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return error ? <p className="rm-error" role="alert">{error}</p> : <p className="rm-empty"><Icon name="loader" spin /> Loading...</p>;
  }

  return (
    <div className="adm-tab">
      {error && <p className="rm-error" role="alert">{error}</p>}
      <ul className="adm-sources">
        {SOURCE_ROWS.map(([id, label, key]) => (
          <li key={id}>
            <span className={`adm-sources__dot${status.sources[id] ? ' on' : ''}`} />
            {label}
            <span className="adm-finding__meta">{status.sources[id] ? 'On' : `Off: needs ${key}`}</span>
          </li>
        ))}
      </ul>
      {!status.sources.model && <p className="rm-error" role="alert">The agents need TILEWARD_API_KEY.</p>}
      {!status.people_can_be_researched && (
        <p className="adm-finding__meta">Companies are researched from their own sites. People need web search or Apollo.</p>
      )}
      <div className="adm-agents-run">
        <p className="adm-finding__meta">
          Blanks are filled in by themselves when the source is strong. Anything less sure, or that would
          change what's on file, waits here for review. Each day the agents take up to {status.daily_limits.subjects} people
          and companies with gaps.
          {' '}Today: {status.used_today.apollo} of {status.daily_limits.apollo} Apollo lookups,
          {' '}{status.used_today.search} of {status.daily_limits.search} searches.
        </p>
        <button type="button" className="rm-button" disabled={busy || working || !status.sources.model} onClick={() => act(enrichmentApi.sweep)}>
          <Icon name={working ? 'loader' : 'search'} spin={working} /> {working ? `${status.queued} to go` : 'Run now'}
        </button>
      </div>

      <h3 className="adm-heading">Waiting for review ({status.waiting_for_review})</h3>
      {findings.length === 0
        ? <p className="rm-empty">Nothing is waiting.</p>
        : (
          <ul className="adm-events">
            {findings.map(finding => (
              <FindingCard
                key={finding.id}
                finding={finding}
                busy={busy}
                onDecide={(item, decision) => act(() => enrichmentApi[decision](item.id))}
              />
            ))}
          </ul>
        )}

      <h3 className="adm-heading">Recent runs</h3>
      {runs.length === 0
        ? <p className="rm-empty">No runs yet.</p>
        : (
          <ul className="adm-events">
            {runs.map(run => (
              <li key={run.id} className="adm-event">
                <div className="adm-event__line">
                  <span className="adm-event__who">{run.label}</span>
                  <span className="adm-event__when" title={formatDateTime(run.created_at)}>{timeAgo(run.created_at)}</span>
                </div>
                <div className="adm-finding__meta">{runProgress(run)}</div>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const AdminPanel = ({ isOpen, onClose }) => {
  const [tab, setTab] = useState('users');
  const dialogRef = useRef(null);

  useEffect(() => {
    if (isOpen) dialogRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="rm-overlay" onClick={onClose} aria-hidden="true" />
      <div className="rm-dialog adm-dialog" role="dialog" aria-modal="true" aria-labelledby="adm-title" ref={dialogRef} tabIndex={-1}>
        <header className="rm-dialog__header">
          <div>
            <h2 id="adm-title"><Icon name="shield" /> Users and activity</h2>
            <p>Who can sign in, every change made in Sinatra, and what the agents are finding.</p>
          </div>
          <button type="button" className="rm-icon-button" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </header>
        <div className="adm-tabs" role="tablist" aria-label="Users and activity">
          {[['users', 'Users'], ['activity', 'Activity'], ['agents', 'Agents'], ['icp', 'ICP'], ['import', 'Import']].map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`adm-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`adm-panel-${id}`}
              className={`adm-tabs__tab${tab === id ? ' active' : ''}`}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="rm-dialog__body" role="tabpanel" id={`adm-panel-${tab}`} aria-labelledby={`adm-tab-${tab}`}>
          {tab === 'users' && <UsersTab />}
          {tab === 'activity' && <ActivityTab />}
          {tab === 'agents' && <AgentsTab />}
          {tab === 'icp' && <IcpSegmentsTab />}
          {tab === 'import' && <LinkedInImportTab />}
        </div>
      </div>
    </>,
    document.body
  );
};

export default AdminPanel;
